/**
 * Notification Composable
 * Provides a centralized notification system using Vuetify Snackbar
 */

import { ref } from 'vue';

// Shared state for notifications
const snackbar = ref(false);
const snackbarMessage = ref('');
const snackbarColor = ref('success');
const snackbarTimeout = ref(3000);
const snackbarIcon = ref('');

export const useNotification = () => {
  /**
   * Show a notification
   * @param {Object} options
   * @param {string} options.message - Message to display
   * @param {string} options.type - 'success', 'error', 'warning', 'info'
   * @param {number} options.timeout - Duration in ms (default: 3000)
   */
  const showNotification = ({ message, type = 'info', timeout = 3000 }) => {
    snackbarMessage.value = message;
    snackbarTimeout.value = timeout;

    // Set color and icon based on type
    switch (type) {
      case 'success':
        snackbarColor.value = 'success';
        snackbarIcon.value = 'mdi-check-circle';
        break;
      case 'error':
        snackbarColor.value = 'error';
        snackbarIcon.value = 'mdi-alert-circle';
        break;
      case 'warning':
        snackbarColor.value = 'warning';
        snackbarIcon.value = 'mdi-alert';
        break;
      case 'info':
      default:
        snackbarColor.value = 'info';
        snackbarIcon.value = 'mdi-information';
        break;
    }

    snackbar.value = true;
  };

  /**
   * Show success notification
   * @param {string} message
   * @param {number} timeout
   */
  const showSuccess = (message, timeout = 3000) => {
    showNotification({ message, type: 'success', timeout });
  };

  /**
   * Show error notification
   * @param {string} message
   * @param {number} timeout
   */
  const showError = (message, timeout = 5000) => {
    showNotification({ message, type: 'error', timeout });
  };

  /**
   * Show warning notification
   * @param {string} message
   * @param {number} timeout
   */
  const showWarning = (message, timeout = 4000) => {
    showNotification({ message, type: 'warning', timeout });
  };

  /**
   * Show info notification
   * @param {string} message
   * @param {number} timeout
   */
  const showInfo = (message, timeout = 3000) => {
    showNotification({ message, type: 'info', timeout });
  };

  /**
   * Close notification
   */
  const closeNotification = () => {
    snackbar.value = false;
  };

  /**
   * Handle error with notification
   * @param {Error} error
   * @param {string} defaultMessage
   */
  const handleError = (error, defaultMessage = 'เกิดข้อผิดพลาด') => {
    const message = error?.message || error?.toString() || defaultMessage;
    showError(message);
    console.error('Error:', error);
  };

  return {
    // State
    snackbar,
    snackbarMessage,
    snackbarColor,
    snackbarTimeout,
    snackbarIcon,

    // Methods
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeNotification,
    handleError,
  };
};
